/**
 * OpenalexCriteriaPanel
 *
 * Two-column search-criteria form for the OpenAlex retrieval modal (Panel 1).
 * Col 1: Keywords (title+abstract / title / full text) + Author
 * Col 2: LLM-suggested OpenAlex topics with checkboxes (grouped by field -> subfield)
 * Stateless: all values and handlers come from OpenalexRetrievalModal via props.
 */

import { useTranslation } from 'react-i18next';
import type { OpenAlexQuery } from '../services/openalexRetrievalService';
import type { OpenAlexSubfieldSuggestion, OpenAlexTopicSuggestion } from '../services/subProjectService';
import { ChipInput, FieldRow, SectionHeading, TextInput } from './OpsRetrievalFormParts';
import type { TopicPhase } from './OpenalexRetrievalModal';

export interface OpenalexCriteriaPanelProps {
  query: OpenAlexQuery;
  set: <K extends keyof OpenAlexQuery>(key: K, value: OpenAlexQuery[K]) => void;
  hasAnyCriteria: boolean;
  previewing: boolean;
  previewError: string | null;
  onClose: () => void;
  onPreview: () => void;
  topicPhase: TopicPhase;
  subfields: OpenAlexSubfieldSuggestion[];
  topicSuggestions: OpenAlexTopicSuggestion[];
  topicError: string | null;
  selectedTopicIds: Set<string>;
  onToggleTopic: (id: string) => void;
  onSelectAllTopics: () => void;
  onDeselectAllTopics: () => void;
}

export default function OpenalexCriteriaPanel({
  query, set,
  hasAnyCriteria, previewing, previewError,
  onClose, onPreview,
  topicPhase, subfields, topicSuggestions, topicError, selectedTopicIds,
  onToggleTopic, onSelectAllTopics, onDeselectAllTopics,
}: OpenalexCriteriaPanelProps) {
  const { t } = useTranslation();

  // Group topic suggestions by field -> subfield for hierarchical display
  type SubfieldMap = Record<string, { label: string; items: OpenAlexTopicSuggestion[] }>;
  type FieldMap = Record<string, { label: string; subfields: SubfieldMap }>;
  const groupedTopics = topicSuggestions.reduce<FieldMap>((acc, s) => {
    const fieldKey = s.field_name || s.domain_name || '?';
    const subKey = s.subfield_name || fieldKey;
    if (!acc[fieldKey]) acc[fieldKey] = { label: fieldKey, subfields: {} };
    if (!acc[fieldKey].subfields[subKey]) acc[fieldKey].subfields[subKey] = { label: subKey, items: [] };
    acc[fieldKey].subfields[subKey].items.push(s);
    return acc;
  }, {});

  return (
    <>
      <div className="px-5 py-3 overflow-y-auto flex-1 text-sm">
        <div className="grid grid-cols-[1fr_2fr] gap-x-6">

          {/* LEFT COLUMN — Keywords + Author */}
          <div>
            <SectionHeading label={t('openalexRetrieval.criteria.keywords', 'Keywords')} />
            <FieldRow label={t('openalexRetrieval.criteria.titleAbstract', 'Title + Abstract')}>
              <TextInput
                placeholder="e.g. solid electrolyte battery"
                value={query.keywords_title_abstract ?? ''}
                onChange={(e) => set('keywords_title_abstract', e.target.value)}
              />
            </FieldRow>
            <FieldRow label={t('openalexRetrieval.criteria.titleOnly', 'Title only')}>
              <TextInput
                placeholder="e.g. fuel cell"
                value={query.keywords_title ?? ''}
                onChange={(e) => set('keywords_title', e.target.value)}
              />
            </FieldRow>
            <FieldRow label={t('openalexRetrieval.criteria.fullText', 'Full text')}>
              <TextInput
                placeholder="e.g. thermal runaway"
                value={query.keywords_fulltext ?? ''}
                onChange={(e) => set('keywords_fulltext', e.target.value)}
              />
            </FieldRow>

            <SectionHeading label={t('openalexRetrieval.criteria.parties', 'Author')} />
            <FieldRow label={t('openalexRetrieval.criteria.author', 'Author name')}>
              <ChipInput
                values={query.author ?? []}
                onChange={(v) => set('author', v)}
                placeholder={t('openalexRetrieval.criteria.authorPlaceholder', 'Name - press Enter')}
              />
            </FieldRow>
          </div>

          {/* RIGHT COLUMN — Topic suggestions */}
          <div className="border-l border-slate-200 pl-4">
            <div className="flex items-center justify-between mb-1.5">
              <SectionHeading label={t('openalexRetrieval.topics.title', 'Suggested OpenAlex Topics')} />
            </div>

            {(topicPhase === 'idle' || topicPhase === 'loop1_loading') && (
              <div className="flex items-center gap-2 py-4 text-slate-400 text-xs">
                <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                {t('openalexRetrieval.topics.loading', 'Analyzing problem statement...')}
              </div>
            )}

            {topicPhase === 'loop2_loading' && (
              <div>
                <p className="text-xs text-slate-600 mb-2">
                  {t('openalexRetrieval.topics.refining', '{{count}} relevant subfields identified. Selecting specific topics - please be patient...', { count: subfields.length })}
                </p>
                <div className="space-y-1 max-h-[52vh] overflow-y-auto pr-1 mb-2">
                  {subfields.map((sf) => (
                    <div key={sf.id} className="text-xs text-slate-500 flex gap-1.5">
                      <span className="text-slate-700 font-medium flex-shrink-0">{sf.display_name}</span>
                      <span className="text-slate-400">({sf.field_name})</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  {t('openalexRetrieval.topics.refiningTopics', 'Selecting specific topics...')}
                </div>
              </div>
            )}

            {topicPhase === 'error' && topicError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-2">{topicError}</p>
            )}

            {topicPhase === 'complete' && topicSuggestions.length === 0 && (
              <p className="text-xs text-slate-400 py-2">{t('openalexRetrieval.topics.empty', 'No suggestions available')}</p>
            )}

            {topicPhase === 'complete' && topicSuggestions.length > 0 && (
              <>
                <div className="flex gap-2 mb-2">
                  <button type="button" className="text-xs text-blue-600 hover:underline" onClick={onSelectAllTopics}>
                    {t('openalexRetrieval.topics.selectAll', 'Select all')}
                  </button>
                  <button type="button" className="text-xs text-slate-500 hover:underline" onClick={onDeselectAllTopics}>
                    {t('openalexRetrieval.topics.deselectAll', 'Deselect all')}
                  </button>
                </div>
                <div className="space-y-3 max-h-[58vh] overflow-y-auto pr-1">
                  {Object.entries(groupedTopics).sort(([a], [b]) => a.localeCompare(b)).map(([field, { subfields: subs }]) => (
                    <div key={field}>
                      <p className="text-xs font-bold text-slate-700 bg-slate-100 px-2 py-1 rounded mb-1.5">{field}</p>
                      {Object.entries(subs).sort(([a], [b]) => a.localeCompare(b)).map(([subfield, { items }]) => (
                        <div key={subfield} className="ml-2 mb-2">
                          <p className="text-xs font-semibold text-slate-500 border-l-2 border-slate-200 pl-2 mb-1">
                            {subfield}
                          </p>
                          <div className="space-y-0.5 pl-1">
                            {items.map((s) => (
                              <label key={s.id} className="flex items-start gap-1.5 text-xs cursor-pointer py-0.5 hover:bg-blue-50 rounded px-1">
                                <input
                                  type="checkbox"
                                  checked={selectedTopicIds.has(s.id)}
                                  onChange={() => onToggleTopic(s.id)}
                                  className="rounded mt-0.5 flex-shrink-0"
                                />
                                <span className="text-slate-700">{s.display_name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {previewError && (
          <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{previewError}</p>
        )}
      </div>

      <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 flex-shrink-0">
        <button type="button" className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200" onClick={onClose}>
          {t('common.cancel', 'Cancel')}
        </button>
        <button
          type="button"
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={!hasAnyCriteria || previewing}
          onClick={onPreview}
        >
          {previewing ? t('openalexRetrieval.criteria.previewing', 'Querying OpenAlex…') : t('openalexRetrieval.criteria.previewBtn', 'Preview Count')}
        </button>
      </div>
    </>
  );
}
